<template>
  <b-modal
    :visible="mapOpen"
    :hide-footer="true"
    :hide-header="true"
    size="xl"
    body-class="!p-1"
    content-class="rounded-none w-full"
    modal-class="md:pt-3"
    centered
    @hide="$emit('closeMap')"
  >
    <div class="map-modal marked-map-modal flex flex-col">
      <ModalCloseButton
        align="outside"
        @click="$emit('closeMap')"
      />
      <div class="grow relative map-modal-map overflow-clip">
        <Mapbox
          ref="mapbox"
          class="absolute"
          :center="center"
          :zoom="zoom"
          size-to-parent-bounds
        >
          <MapboxMarker
            v-for="({ day, lng, lat }, i) in tripLocations"
            :key="`${lng}-${lat}-${day}-${i}`"
            :coordinates="[lng, lat]"
            :active="day === 'x' || day === activeMarker || day === highlightedMarker"
            :z-index="day === highlightedMarker ? 999 : null"
            :active-text="day === 'x' ? null : day"
          />
          <MapboxNavigationControl />
        </Mapbox>
      </div>
      <div
        v-if="activeItinerary.itinerary.length > 1"
        class="product-featured-itinerary mt-4 px-2 md:px-4 lg:px-5 pb-1"
      >
        <div class="flex flex-wrap -mx-4">
          <div
            v-for="(itineraryDay, idx) in filteredItinerary"
            :key="'tcl' + idx"
            class="w-12/12 md:w-4/12 xl:w-3/12 px-4"
          >
            <div
              class="itinerary-item itinerary-item-map rounded-[1.25rem]"
              :class="itineraryDay.map_points?.length ? 'cursor-pointer' : 'cursor-default'"
              @click="itineraryDay.map_points.length ? $emit('setMarker', itineraryDay.day) : null"
              @mouseover="highlightedMarker = itineraryDay.day"
              @mouseout="highlightedMarker = null"
            >
              <div class="flex">
                <div
                  v-if="!isDestination"
                  class="itinerary-item-day text-nowrap"
                  :class="{ 'highlighted-itinerary': itineraryDay.day == activeMarker }"
                >
                  <span class="itinerary-item-day-label font-bold">
                    {{ $t('day') }} {{ itineraryDay.day }}
                  </span>&nbsp;-&nbsp;
                </div>
                <div
                  class="itinerary-item-title"
                  :class="{ 'font-bold': isDestination }"
                >
                  {{ itineraryDay.title }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default defineNuxtComponent({
  props: {
    activeItinerary: {
      type: Object,
      required: true,
    },

    activeMarker: {
      type: String,
      default: null,
    },

    mapOpen: {
      type: Boolean,
      default: false,
    },

    trip: {
      type: Object,
      required: true,
    },

    isDestination: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['setMarker', 'closeMap'],

  data () {
    return {
      highlightedMarker: null,
      mapKey: 1,
      timer: null,
    }
  },

  computed: {
    zoom () {
      return this.activeItinerary?.overviewmap?.thumb_bounds?.zoom || 4
    },

    tripLocations () {
      if (!this.activeItinerary?.overviewmap) {
        const tripLocations = []

        this.activeItinerary.itinerary
          ?.forEach(d => d.map_points
            ?.forEach(p => tripLocations.push({
              lng: parseFloat(p.lng),
              lat: parseFloat(p.lat),
              day: d.day,
            })))

        return tripLocations
      }

      if (!this.activeItinerary?.overviewmap.points?.length && this.activeItinerary.overviewmap.thumb_bounds) {
        const { lng_center: lng, lat_center: lat } = this.activeItinerary?.overviewmap.thumb_bounds ?? []
        return [{ lng, lat }]
      }

      return this.activeItinerary?.overviewmap.points.map((p) => {
        return {
          lng: parseFloat(p.lng),
          lat: parseFloat(p.lat),
          day: p.dayname,
        }
      })
    },

    center () {
      if (this.tripLocations?.length && this.tripLocations.length < 2) {
        const center = this.tripLocations[0]
        return [center.lng, center.lat]
      }

      // Use first itinerary with map points as center
      if (!this.activeItinerary?.overviewmap?.thumb_bounds?.lat_center) {
        const itineraryWithMap = this.activeItinerary?.itinerary?.find(d => d.map_points)
        const firstValidMapPoints = itineraryWithMap?.map_points?.find(p => p.lng !== undefined && p.lat !== undefined)

        if (!firstValidMapPoints) {
          return null
        }

        return [firstValidMapPoints.lng, firstValidMapPoints.lat]
      }

      return [
        parseFloat(this.activeItinerary?.overviewmap?.thumb_bounds.lng_center),
        parseFloat(this.activeItinerary?.overviewmap?.thumb_bounds.lat_center),
      ]
    },

    filteredItinerary () {
      return this.activeItinerary?.itinerary.filter(i => i.day !== 'x')
    },

    itineraryDayColumns () {
      const trips = []
      const pageSize = 3

      for (let i = 0; i < this.activeItinerary?.itinerary.length / pageSize; i++) {
        trips.push(this.activeItinerary?.itinerary.slice(i * pageSize, (i + 1) * pageSize))
      }

      return trips
    },
  },

  watch: {
    mapOpen () {
      if (this.mapOpen) {
        this.$nextTick(() => {
          if (this.$refs.mapbox) {
            this.$refs.mapbox?.onload(() => this.flyToMarker(this.activeMarker))
          }
        })
      }
    },

    activeMarker (day) {
      this.flyToMarker(day)
    },
  },

  beforeUnmount () {
    clearTimeout(this.timer)
  },

  methods: {
    flyToMarker (day) {
      if (!this.$refs.mapbox?.map) {
        return
      }

      const marker = this.activeItinerary?.itinerary?.find(d => d.day === day && d.map_points.length > 0)

      if (!marker) {
        return
      }

      this.$refs.mapbox.map.flyTo({
        center: [
          parseFloat(marker.map_points[0].lng),
          parseFloat(marker.map_points[0].lat),
        ],
        zoom: 10,
      })
    },
  },
})
</script>

<style lang="scss">
.map-modal {
  &.marked-map-modal {
    min-height: 85vh;
  }

  .map-modal-map {
    min-height: 500px;
  }
}
</style>
